import React, { useCallback, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  useFloating,
  useHover,
  useInteractions,
  useDismiss,
  offset,
  flip,
  shift,
  autoUpdate,
  safePolygon,
} from "@floating-ui/react";
import "./silos.css";
import IconDb from "../../common/icons/IconDb";
import IconCheck from "../../common/icons/IconCheck";
import { OECCBB_API_BASE_URL } from "../../config/index";

const GlobeIcon = ({ className, ...props }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...props}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2c2.5 2.5 4 6 4 10s-1.5 7.5-4 10c-2.5-2.5-4-6-4-10s1.5-7.5 4-10z" />
  </svg>
);

const CloseIcon = ({ className, ...props }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z"
      stroke="#6B7280"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 10.6667V8"
      stroke="#6B7280"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 5.33334H8.00667"
      stroke="#6B7280"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

// SiloCard: NeonAI-style silo card for DB silos
function SiloCard({
  res,
  selected,
  onToggle,
  inPortal,
  token,
  disabled = false,
}) {
  const title = res.title || res.label || res.name || "Silo";
  const authorsList = res.authors || [];
  const authors = authorsList.length
    ? authorsList
        .slice(0, 2)
        .map((a) => `${a.firstName} ${a.lastName}`)
        .join(", ") + (authorsList.length > 2 ? "..." : "")
    : null;
  const coverUrl = res.coverUrl || res.coverImageUrl || null;
  const daysRemaining = calculateDaysRemaining(res.expireAt);
  const hasTooltipContent = res.expireAt || res.expired;
  const isWarning = daysRemaining !== null && daysRemaining < 30;

  const [isOpen, setIsOpen] = useState(false);

  const { x, y, strategy, refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "top",
    // The portal makes this necessary.
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8), // space between trigger and tooltip
      flip({ fallbackAxisSideDirection: "end" }), // flip to bottom if not enough space on top
      shift({ padding: 8 }), // ensure it's within viewport
    ],
  });

  const hover = useHover(context, {
    handleClose: safePolygon({
      buffer: 1,
    }),
    delay: { open: 0, close: 150 },
  });

  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
  ]);

  const TooltipComponent = (
    <div
      ref={refs.setFloating}
      style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
      className={`tooltip visible ${inPortal ? "portal" : ""}`}
      {...getFloatingProps()}
    >
      <div className="tooltip-content">
        <p
          className={`tooltip-expiration ${
            // eslint-disable-line
            isWarning || res.expired ? "warning" : ""
          }`}
        >
          {title === "General"
            ? "Vous devez renouveler votre souscription pour continuer à utiliser le chat."
            : res.expired
            ? "Pour continuer à y accéder, veuillez effectuer votre achat."
            : `Il vous reste encore ${daysRemaining} jours d'accès à cette ressource.`}
        </p>
        {!res.expired && res.sellingUrl && (
          <a
            href={res.sellingUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "none" }}
            className="tooltip-button"
          >
            Renouveler votre achat
          </a>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`silo-item ${
        res.expired
          ? "state-expired"
          : selected
          ? "state-selected"
          : "state-not-selected"
      }`}
    >
      <div className="silo-thumbnail">
        {coverUrl ? (
          <img src={coverUrl} alt={title} />
        ) : (
          <div className="thumbnail-placeholder">📘</div>
        )}
      </div>
      {res.organization?.avatarUrl && (
        <img
          src={res.organization.avatarUrl}
          className="organization-logo"
          alt={res.organization.name}
        />
      )}
      <div className="silo-details">
        <h3 className="silo-title">{title}</h3>
        {authors && <p className="silo-authors">{authors}</p>}
      </div>
      <div className="silo-toggle-overlay">
        {hasTooltipContent && (
          <div
            className="tooltip-container"
            ref={refs.setReference}
            {...getReferenceProps()}
          >
            {res.expired && (
              <span className="remaining-days-text expired-text">
                Accès expiré
              </span>
            )}
            {!res.expired && daysRemaining !== null && daysRemaining > 0 && (
              <span
                className={`remaining-days-text ${isWarning ? "warning" : ""}`}
              >
                reste {daysRemaining} j.
              </span>
            )}
            <div className="info-button">
              <InfoIcon />
            </div>
            {isOpen &&
              (inPortal
                ? createPortal(TooltipComponent, document.body)
                : TooltipComponent)}
          </div>
        )}

        {res.expired ? (
          <button
            className="acheter-button"
            disabled={!res.sellingUrl}
            title={!res.sellingUrl ? "Non disponible" : ""}
            onClick={() => {
              if (title === "General") {
                const url = `${OECCBB_API_BASE_URL}/fr/contributions?token=${token}`;
                window.open(url, "_blank", "noopener,noreferrer");
              } else if (res.sellingUrl) {
                window.open(res.sellingUrl, "_blank", "noopener,noreferrer");
              }
            }}
          >
            Acheter
          </button>
        ) : (
          <label className="switch">
            <input type="checkbox" checked={selected} onChange={onToggle} />
            <span className="slider round"></span>
          </label>
        )}
      </div>
    </div>
  );
}

const calculateDaysRemaining = (expireAt) => {
  if (!expireAt) return null;
  const now = new Date();
  const expireDate = new Date(expireAt);
  const diffTime = expireDate - now;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export default function Silos({
  userSilots = [],
  setSilos: setParentSilos,
  user,
  token,
  externalTrigger,
}) {
  const [activeSource, setActiveSource] = useState(() => {
    // Default to 'db' if there are any non-expired silos available.
    const hasDbSilos = userSilots.some((s) => !s.expired);
    try {
      const saved = sessionStorage.getItem("selectedSilos");
      const parsed = saved ? JSON.parse(saved) : [];
      // If there are saved silos, default to 'db'. Otherwise, respect available silos.
      return parsed.length > 0 ? "db" : hasDbSilos ? "db" : "web";
    } catch {
      return hasDbSilos ? "db" : "web";
    }
  });
  const [showModal, setShowModal] = useState(false);
  
  useEffect(() => {
    if (externalTrigger) {
      setShowModal(true);
    }
  }, [externalTrigger]);
  const allSilos = userSilots;
  const [selectedSilos, setSelectedSilos] = useState(() => {
    const allNonExpired = userSilots.filter((s) => !s.expired);
    try {
      const saved = sessionStorage.getItem("selectedSilos");
      if (saved) {
        const parsed = JSON.parse(saved);
        const validSaved = parsed.filter((p) => {
          const pName = p.name || p;
          return allNonExpired.some((s) => (s.name || s) === pName);
        });

        if (validSaved.length > 0) {
          return validSaved.map((s) => s.name || s);
        }
      }
    } catch {
      // Error parsing, proceed to select a default.
    }

    // If no valid saved silos, or if storage is empty/invalid, select all non-expired silos
    if (allNonExpired.length > 0) {
      return allNonExpired.map((s) => s.name || s);
    }
    return [];
  });
  const expiredCount = allSilos.filter((s) => s.expired).length;
  const selectableCount = allSilos.length - expiredCount;
  const hasInitialized = useRef(false);

  const handleToggle = (source) => {
    if (activeSource === "web" && source === "db") {
      setShowModal(true);
      return;
    }
    if (activeSource === source) {
      setShowModal(true);
      return;
    }
    hasInitialized.current = true;
    setActiveSource(source);
    if (source === "web") {
      setParentSilos([{ name: "web_search", label: "Web Search" }]);
    } else {
      const saved = sessionStorage.getItem("selectedSilos");
      const parsed = saved ? JSON.parse(saved) : [];
      if (parsed.length > 0) {
        setParentSilos(parsed);
      } else {
        setParentSilos([]);
      }
    }
  };

  const handleSiloClick = (silo) => {
    setSelectedSilos((prev) =>
      prev.includes(silo) ? prev.filter((s) => s !== silo) : [...prev, silo]
    );
  };

  const handleSelectAll = useCallback(() => {
    const allValidSiloNames = allSilos
      .filter((s) => !s.expired)
      .map((s) => s.name || s);
    setSelectedSilos(allValidSiloNames);
  }, [allSilos]);

  const handleDeselectAll = useCallback(() => {
    setSelectedSilos([]);
  }, []);

  useEffect(() => {
    if (
      !hasInitialized.current &&
      allSilos.length > 0 &&
      selectedSilos.length === 0 &&
      activeSource === "db"
    ) {
      const saved = sessionStorage.getItem("selectedSilos");
      const parsed = saved ? JSON.parse(saved) : [];
      if (!saved || (Array.isArray(parsed) && parsed.length === 0)) {
        const allNonExpired = allSilos
          .filter((s) => !s.expired)
          .map((s) => s.name || s);
        if (allNonExpired.length > 0) {
          setSelectedSilos(allNonExpired);
        }
      }
      hasInitialized.current = true;
    }
  }, [allSilos.length, selectedSilos.length, activeSource]);
  // Update parent state when selectedSilos change
  useEffect(() => {
    const fullSilos = selectedSilos
      .map((name) => allSilos.find((s) => (s.name || s) === name))
      .filter(Boolean);

    sessionStorage.setItem("selectedSilos", JSON.stringify(fullSilos));
    if (activeSource === "db") {
      setParentSilos(fullSilos);
    }
  }, [selectedSilos, allSilos, setParentSilos, activeSource]);

  useEffect(() => {
    if (activeSource === "web" && selectedSilos.length > 0) {
      setSelectedSilos([]);
    }
  }, [activeSource, selectedSilos.length]);

  // Set initial silo state on component mount
  useEffect(() => {
    if (activeSource === "web") {
      setParentSilos([{ name: "web_search", label: "Web Search" }]);
    } else {
      const saved = sessionStorage.getItem("selectedSilos");
      const parsed = saved ? JSON.parse(saved) : [];
      if (parsed.length > 0) {
        setParentSilos(parsed);
      } else {
        const allNonExpired = allSilos.filter((s) => !s.expired);
        if (allNonExpired.length > 0) {
          setParentSilos(allNonExpired); // Default to all non-expired silos
        } else {
          setParentSilos([]);
        }
      }
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  return (
    // Main Silos component render
    <>
      {/* Control bar */}
      <div className="silos-container">
        {/* DB toggle button */}
        <button
          onClick={() => handleToggle("db")}
          className="silos-toggle-button"
        >
          <IconDb className="silos-icon" />
        </button>

        {/* Silos button */}
        {activeSource === "web" ? (
          <button className="silos-button">Recherche Web</button>
        ) : (
          <button className="silos-button">
            {`${selectedSilos.length}/${allSilos.length} ressources activées`}
          </button>
        )}
      </div>

      {/* Modal */}
      {showModal &&
        createPortal(
          <div
            className="silo-modal-overlay"
            onClick={() => setShowModal(false)}
          >
            <div
              className="silo-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="silo-modal-header">
                <div className="silo-modal-title-container">
                  <IconDb className="silo-modal-db-icon" />
                  <h2 className="silo-modal-title">Liste des ressources :</h2>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="silo-modal-close-button-icon"
                >
                  <CloseIcon />
                </button>
              </div>

              <div className="silo-modal-controls">
                <div className="silo-modal-actions">
                  <label className="switch-label">Tout sélectionner</label>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={
                        activeSource === "web"
                          ? false
                          : selectedSilos.length === selectableCount
                      }
                      onChange={(e) => {
                        if (activeSource === "web") {
                          setActiveSource("db");
                          const saved = sessionStorage.getItem("selectedSilos");
                          const parsed = saved ? JSON.parse(saved) : [];
                          if (parsed.length > 0) {
                            setParentSilos(parsed);
                          } else {
                            setParentSilos([]);
                          }
                          if (e.target.checked) {
                            handleSelectAll();
                          }
                        } else {
                          e.target.checked
                            ? handleSelectAll()
                            : handleDeselectAll();
                        }
                      }}
                    />
                    <span className="slider round"></span>
                  </label>
                  <button
                    onClick={() => {
                      setActiveSource("web");
                      setParentSilos([
                        { name: "web_search", label: "Web Search" },
                      ]);
                      setShowModal(false);
                    }}
                    className={`silo-modal-web-button ${
                      activeSource === "web" ? "active" : ""
                    }`}
                  >
                    <GlobeIcon className="silo-modal-web-icon" />
                    Recherche web
                  </button>
                </div>
                <div className="footer-stats">
                  <span className="selection-count">
                    {selectedSilos.length}/{allSilos.length} sélectionnées
                  </span>
                  {expiredCount > 0 && (
                    <>
                      <span className="stats-separator">/</span>
                      <span className="expired-count">
                        {expiredCount} expirées
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="silo-list">
                {allSilos.map((res) => (
                  <SiloCard
                    key={res.id || res.name}
                    res={res}
                    selected={
                      activeSource === "web"
                        ? false
                        : selectedSilos.includes(res.name || res)
                    }
                    onToggle={() => {
                      if (activeSource === "web") {
                        setActiveSource("db");
                        const saved = sessionStorage.getItem("selectedSilos");
                        const parsed = saved ? JSON.parse(saved) : [];
                        if (parsed.length > 0) {
                          setParentSilos(parsed);
                        } else {
                          setParentSilos([]);
                        }
                        handleSiloClick(res.name || res);
                      } else {
                        handleSiloClick(res.name || res);
                      }
                    }}
                    inPortal
                    user={user}
                    token={token}
                    disabled={false}
                  />
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
